import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchInvoiceDetails } from "../services/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMoneyCheck,
  faFilePdf,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import "../styles/InvoicePage.css";
import axios from "axios";
import { BASE_API_URL, Rezorpay_Key } from "../services/Razorpay";

const InvoicePage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const myQueryParam = queryParams.get("oid");

  const [orderData, setOrderData] = useState({
    invoiceID: 0,
    orderID: 0,
    orderNo: "",
    orderDate: "",
    subTotal: 0,
    taxAmount: 0,
    totalAmount: 0,
    paidAmount: 0,
    remainingAmount: 0,
    disPer: 0,
    discountAmt: 0,
    beforDisAmt: 0,
    isDiscount: false,
    noOfItems: 0,
    customerName: "",
    mobileNumber: "",
    email: "",
    address1: "",
    testTypeID: 0,
    testTypeName: "",
    testCategoryID: 0,
    testCategoryName: "",
    labID: 0,
    labName: "",
    offerPercentage: 0,
    offerValue: 0,
    responseObject: [],
  });

  useEffect(() => {
    fetchItemData();
  }, []);

  const fetchItemData = async () => {
    setIsFetching(true);
    try {
      const response = await fetchInvoiceDetails(myQueryParam);
      if (response.data.isSuccess) {
        setOrderData(response.data);
      } else {
        setOrderData((prev) => ({ ...prev, responseObject: [] }));
      }
    } catch (error) {
      console.error("Error fetching order data:", error);
      navigate("/error");
    } finally {
      setIsFetching(false);
    }
  };

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      // Avoid loading the script twice if already present
      if (document.querySelector('script[src*="checkout.razorpay.com"]')) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const handlePayment = async () => {
    // Guard: don't allow double-click
    if (isLoading) return;
    setIsLoading(true);

    const res = await loadRazorpayScript();
    if (!res) {
      alert("Razorpay SDK failed to load. Are you online?");
      setIsLoading(false);
      return;
    }

    // Guard: remaining amount must be valid
    if (!orderData.remainingAmount || orderData.remainingAmount <= 0) {
      alert("No pending amount to pay.");
      setIsLoading(false);
      return;
    }

    try {
      // Step 1: Create order on backend
      const orderResponse = await axios.post(
        `${BASE_API_URL}/LLCPay/SetPaymentByLinkForOrder`,
        {
          orderID: myQueryParam,
          invoiceID: orderData.invoiceID,
          amt: orderData.remainingAmount,
          currency: "INR",
        },
      );

      if (
        !orderResponse.data ||
        !orderResponse.data.isSuccess ||
        !orderResponse.data.razorOrderNo
      ) {
        alert("Failed to create order. Please try again.");
        setIsLoading(false);
        return;
      }

      const tmpRazorOrderNo = orderResponse.data;

      // Step 2: Open Razorpay
      const options = {
        key: Rezorpay_Key,
        amount: orderData.remainingAmount * 100, // paise
        currency: "INR",
        name: "LIFE LAB CARE",
        description: `Invoice #${orderData.invoiceID} | Order ${orderData.orderNo}`,
        image: `${BASE_API_URL}/media/logos/LLC_logo_New-removebg.png`,
        order_id: tmpRazorOrderNo.razorOrderNo,
        handler: async function (response) {
          // Step 3: Verify payment on backend
          try {
            const verificationResponse = await axios.post(
              `${BASE_API_URL}/LLCPay/UpdateOrderRazBalance`,
              {
                orderID: myQueryParam,
                invoiceID: orderData.invoiceID,
                razorTransactionID: response.razorpay_payment_id,
                razorOrderNo: tmpRazorOrderNo.razorOrderNo,
                receiptNo: tmpRazorOrderNo.receiptNo,
              },
            );

            if (verificationResponse.data.isSuccess) {
              await fetchItemData();
              alert("Payment verified successfully!");
            } else {
              alert("Payment verification failed. Please contact support.");
            }
          } catch (error) {
            console.error("Verification error:", error);
            alert("Payment verification error: " + error.message);
          } finally {
            setIsLoading(false);
          }
        },
        prefill: {
          name: orderData.customerName || "",
          email: orderData.email || "",
          contact: orderData.mobileNumber || "",
        },
        notes: {
          orderID: myQueryParam,
          invoiceID: orderData.invoiceID,
          address: orderData.address1 || "Corporate Office",
        },
        theme: {
          color: "#00C170", // LLC green — visible in Razorpay modal
        },
        modal: {
          ondismiss: function () {
            // User closed modal without paying
            setIsLoading(false);
            alert("Payment was canceled. No amount was charged.");
          },
          escape: false, // prevent accidental ESC close
          backdropclose: false, // prevent click-outside close
        },
      };

      const paymentObject = new window.Razorpay(options);

      // Handle Razorpay-side errors (e.g. card declined)
      paymentObject.on("payment.failed", function (response) {
        console.error("Payment failed:", response.error);
        alert(
          `Payment failed: ${response.error.description}. Please try again or use a different payment method.`,
        );
        setIsLoading(false);
      });

      paymentObject.open();
    } catch (error) {
      console.error("Payment processing error:", error);
      alert(
        "An error occurred while processing your payment. Please try again.",
      );
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="invoice-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
        <span>Loading order details...</span>
      </div>
    );
  }

  return (
    <div className="invoice-container">
      {/* ── Action Bar ── */}
      <div className="inv-action-bar">
        <div className="inv-brand">
          <div className="inv-brand-dot"></div>
          <span className="inv-brand-name">Life Lab Care — Invoice</span>
        </div>
        <div className="inv-btn-group">
          {orderData.remainingAmount == 0 && (
            <button
              className="inv-btn secondary"
              onClick={() => navigate(`/pdf?oid=${myQueryParam}`)}
            >
              <FontAwesomeIcon icon={faFilePdf} />
              PDF
            </button>
          )}
          {orderData.remainingAmount > 0 && (
            <button
              className="inv-btn primary"
              onClick={handlePayment}
              disabled={isLoading}
            >
              <FontAwesomeIcon
                icon={isLoading ? faSpinner : faMoneyCheck}
                spin={isLoading}
              />
              {isLoading
                ? "Processing..."
                : `Pay Now — ₹${orderData.remainingAmount}`}
            </button>
          )}
        </div>
      </div>

      {/* ── Info Sections ── */}
      <div className="inv-info-wrap">
        <div className="inv-info-section">
          <div className="inv-section-head">
            <div className="inv-section-dot"></div>
            <span className="inv-section-title">Order info</span>
          </div>
          <div className="inv-fields">
            <div className="inv-field">
              <span className="inv-f-label">Order no</span>
              <span className="inv-f-value">{orderData.orderNo || "—"}</span>
            </div>
            <div className="inv-field">
              <span className="inv-f-label">Date</span>
              <span className="inv-f-value">{orderData.orderDate || "—"}</span>
            </div>
            <div className="inv-field">
              <span className="inv-f-label">Customer</span>
              <span className="inv-f-value">
                {orderData.customerName || "—"}
              </span>
            </div>
            <div className="inv-field">
              <span className="inv-f-label">Mobile</span>
              <span className="inv-f-value">
                {orderData.mobileNumber || "—"}
              </span>
            </div>
            <div className="inv-field full">
              <span className="inv-f-label">Email</span>
              <span className="inv-f-value">{orderData.email || "—"}</span>
            </div>
          </div>
        </div>

        <div className="inv-info-section">
          <div className="inv-section-head">
            <div className="inv-section-dot"></div>
            <span className="inv-section-title">Lab &amp; test info</span>
          </div>
          <div className="inv-fields">
            <div className="inv-field">
              <span className="inv-f-label">Lab</span>
              <span className="inv-f-value">{orderData.labName || "—"}</span>
            </div>
            <div className="inv-field">
              <span className="inv-f-label">Items</span>
              <span className="inv-f-value">{orderData.noOfItems || "—"}</span>
            </div>
            <div className="inv-field">
              <span className="inv-f-label">Test type</span>
              <span className="inv-f-value">
                {orderData.testTypeName || "—"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Line Items Table ── */}
      {/* Desktop table */}
      <div className="table-responsive">
        <table className="invoice-table">
          <thead>
            <tr>
              <th style={{ width: "5%" }}>Sr.</th>
              <th style={{ width: "18%" }}>Category</th>
              <th style={{ width: "37%" }} className="th-left">
                Test name
              </th>
              <th style={{ width: "12%" }}>Rate</th>
              <th style={{ width: "6%" }}>Qty</th>
              <th style={{ width: "14%" }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {orderData.responseObject.length > 0 ? (
              orderData.responseObject.map((item, index) => (
                <tr key={item.orderDetailID}>
                  <td>{index + 1}</td>
                  <td>{item.testCategoryName}</td>
                  <td className="td-left">{item.testName}</td>
                  <td>₹ {item.rateOnOrder}</td>
                  <td>{item.qty}</td>
                  <td>₹ {item.lineTotal}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="td-empty">
                  No data available
                </td>
              </tr>
            )}

            {orderData.isDiscount && (
              <tr className="summary-row">
                <td colSpan="5" className="summary-label">
                  Before discount
                </td>
                <td>₹ {orderData.beforDisAmt}</td>
              </tr>
            )}
            <tr className="summary-row">
              <td colSpan="5" className="summary-label">
                Sub total
              </td>
              <td>₹ {orderData.offerPercentage > 0 ? orderData.offerValue + orderData.subTotal : orderData.subTotal}</td>
            </tr>
            {orderData.isDiscount && (
              <tr className="summary-row discount-row">
                <td colSpan="5" className="summary-label">
                  Discount ({orderData.disPer}%)
                </td>
                <td>− ₹ {orderData.discountAmt}</td>
              </tr>
            )}
            {orderData.offerPercentage > 0 && (
              <tr className="summary-row discount-row">
                <td colSpan="5" className="summary-label">
                  Offer ({orderData.offerPercentage}%)
                </td>
                <td>− ₹ {orderData.offerValue}</td>
              </tr>
            )}
            <tr className="summary-row total-row">
              <td colSpan="5" className="summary-label">
                Total amount (INR)
              </td>
              <td>₹ {orderData.totalAmount}</td>
            </tr>
            <tr className="summary-row paid-row">
              <td colSpan="5" className="summary-label">
                Paid amount
              </td>
              <td>₹ {orderData.paidAmount}</td>
            </tr>
            <tr className="summary-row remaining-row">
              <td colSpan="5" className="summary-label">
                Remaining amount
              </td>
              <td>₹ {Number(orderData.remainingAmount).toLocaleString('en-IN')}</td>
              {/* <td>₹ {orderData.remainingAmount}</td> */}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Mobile cards — only visible on small screens via CSS */}
      <div className="mobile-items">
        {orderData.responseObject.length > 0 ? (
          orderData.responseObject.map((item, index) => (
            <div key={item.orderDetailID} className="mobile-item-card">
              <div className="mobile-item-header">
                <span className="mobile-item-name">{item.testName}</span>
                <span className="mobile-item-price">₹ {item.lineTotal}</span>
              </div>
              <div className="mobile-item-meta">
                <span className="mobile-item-tag">{item.testCategoryName}</span>
                <span>Rate: ₹{item.rateOnOrder}</span>
                <span>Qty: {item.qty}</span>
              </div>
            </div>
          ))
        ) : (
          <p
            style={{
              textAlign: "center",
              color: "var(--llc-muted)",
              padding: "20px 0",
            }}
          >
            No data available
          </p>
        )}

        <div className="mobile-summary">
          {orderData.isDiscount && (
            <>
              <div className="mobile-sum-row">
                <span className="sum-l">Before discount</span>
                <span>₹ {orderData.beforDisAmt}</span>
              </div>
              <div className="mobile-sum-row">
                <span className="sum-l">Discount ({orderData.disPer}%)</span>
                <span style={{ color: "var(--llc-green-dark)" }}>
                  − ₹ {orderData.discountAmt}
                </span>
              </div>
            </>
          )}
          <div className="mobile-sum-row">
            <span className="sum-l">Sub total</span>
            <span>₹ {orderData.subTotal}</span>
          </div>
          <div className="mobile-sum-row total-row">
            <span className="sum-l">Total amount</span>
            <span>₹ {orderData.totalAmount}</span>
          </div>
          <div className="mobile-sum-row paid-row">
            <span className="sum-l">Paid amount</span>
            <span>₹ {orderData.paidAmount}</span>
          </div>
          <div className="mobile-sum-row remaining-row">
            <span className="sum-l">Remaining</span>
            <span>₹ {orderData.remainingAmount}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePage;
